/**
 * Tenant dashboard V2 copy (COL-533).
 *
 * Same shape and rules as `tenant-dashboard-shell-copy.ts`: pure data, both
 * halves key-for-key identical, imported by the page for its EN/DE fallback
 * and by the i18n seeding script so these reach `public.translations`.
 *
 * Every string the V2 dashboard renders belongs here rather than inline, so
 * the six-language release (COL-477) picks it up — Neel's decision was to ship
 * the two together, which only works if the new copy is translatable from the
 * start instead of being retrofitted.
 */

export function getTenantDashboardV2Labels(lang: "en" | "de") {
  return lang === "de"
    ? {
        // Tabs — tenant
        tabOverview: "Übersicht",
        tabRoom: "Mein Zimmer",
        tabCommunity: "Community",
        tabReferrals: "Empfehlungen",
        tabHelp: "Hilfe",
        // Tabs — applicant
        tabApplications: "Bewerbungen",
        tabSaved: "Favoriten",
        // Hero
        greetingMorning: "Guten Morgen, {name}.",
        greetingAfternoon: "Guten Tag, {name}.",
        greetingEvening: "Guten Abend, {name}.",
        nextStepEyebrow: "Dein nächster Schritt",
        allDoneEyebrow: "Alles erledigt",
        allDoneTitle: "Willkommen zu Hause.",
        allDoneBody:
          "Gerade steht nichts an. Wenn etwas klemmt, meld es unter Hilfe.",
        openAction: "Jetzt erledigen",
        // The next-step headline. These live here rather than in
        // `nextActionSummary`, which returns hardcoded de/en strings: V2 makes
        // this the most prominent line on the dashboard, and the six-language
        // release cannot reach a literal buried in a lib function.
        actionSign: "Dein Mietvertrag ist bereit.",
        actionDeposit: "Kaution zahlen und SEPA einrichten.",
        actionOverdue: "Miete ist überfällig",
        actionMoveIn: "Du ziehst am {date} ein.",
        actionMoveOut: "Auszug am {date}.",
        /*
          New keys rather than edits to the four above (Neel, 23 Oct).

          `public.translations` already carries rows for the existing keys, and
          a DB row wins over the literal here — so rewording `actionMoveIn` in
          this file would change nothing on a seeded environment. The split is
          also the honest one: "Du ziehst am {date} ein" is right while the
          move-in is still ahead, and wrong once the protocol is open.
        */
        actionMoveInReport: "Fülle dein Einzugsprotokoll aus.",
        actionKeyReturn: "Schlüssel zurückgeben.",
        actionSupport: "Antwort vom Support lesen",
        actionReferralReward: "Empfehlungsprämie klären",
        // Room summary
        roomAt: "{room} · {address}",
        moveInOn: "Einzug am {date}",
        // States
        loading: "Wird geladen …",
        loadFailed:
          "Dein Dashboard konnte nicht geladen werden. Lade die Seite neu.",
        noBooking:
          "Für dein Konto liegt noch keine Buchung vor. Sobald deine Bewerbung angenommen ist, siehst du hier deinen Vertrag.",
        openClassicView: "Bewerbungen und Favoriten im bisherigen Dashboard",
        // Settings -> Dokumente. V2 does not own document upload; this points
        // at the V1 screen that does, rather than hiding the tab entirely.
        settingsDocumentsHandoff:
          "Deine Unterlagen verwaltest du vorerst im bisherigen Dashboard.",
        settingsDocumentsOpen: "Unterlagen öffnen",
        // Footer. The dashboard carries its own — the marketing footer's
        // sitemap, socials and viewing CTA are for visitors, not residents.
        footerTenantTitle: "Etwas stimmt nicht? Sag uns Bescheid.",
        footerTenantAction: "Problem melden",
        footerApplicantTitle: "Noch nicht das Richtige gefunden?",
        footerApplicantAction: "Zimmer finden",
        footerWriteUs: "Schreib uns",
        footerCompany: "coliviq GmbH · Elvirastraße 10, 80636 München",
        footerPrivacy: "Datenschutz",
        footerImprint: "Impressum",
        footerTerms: "AGB",
        footerCookieSettings: "Cookie-Einstellungen",
        footerLogout: "Abmelden",
        // Applicant frame (Bewerbungen / Favoriten)
        applicationsHeading: "Deine Bewerbungen",
        applicationsEmpty:
          "Du hast noch keine Bewerbung laufen. Such dir ein Zimmer und bewirb dich in wenigen Minuten.",
        applicationsFindRoom: "Zimmer ansehen",
        stepSubmitted: "Eingereicht",
        stepInReview: "In Prüfung",
        stepOffer: "Zusage",
        stepContract: "Vertrag",
        // "warm" is the German rental term for rent including utilities;
        // "all-in" read like marketing. English keeps "all-in".
        allInFrom: "{price} warm · ab {date}",
        allIn: "{price} warm",
        allInFree: "{price} warm · frei ab {date}",
        documentsHeading: "Deine Unterlagen",
        documentsProgress:
          "{done} von {total} hochgeladen. Vollständige Unterlagen verbessern deine Chancen auf ein Zimmer.",
        documentsIdentity: "Ausweis",
        documentsIdentityHint: "Vorder- und Rückseite",
        documentsIncome: "Einkommensnachweis",
        documentsIncomeHint:
          "Letzte 3 Gehaltsabrechnungen. Studierende: Immatrikulationsbescheinigung oder Bürge",
        documentsFilesCount: "{count} Dateien",
        documentsOneFile: "1 Datei",
        documentsManage: "Unterlagen verwalten",
        documentsAddFile: "Weitere Datei hinzufügen",
        documentsAddFirstFile: "Datei hinzufügen",
        documentsUploading: "Wird hochgeladen …",
        documentsUploadFailed:
          "Die Datei konnte nicht hochgeladen werden. Bitte versuch es noch einmal.",
        documentsAccepted: "PDF oder Bild, bis 10 MB.",
        documentsRemoveElsewhere:
          "Du möchtest eine Datei entfernen? Schreib uns über Hilfe.",
        savedHeading: "Gemerkte Zimmer",
        savedEmpty:
          "Du hast noch keine Zimmer gemerkt. Tippe beim Stöbern auf das Herz, dann liegen sie hier.",
        // Übersicht hero (COL-533 visual port)
        heroFromDate: "Dein Zimmer ab {date}.",
        heroMoveInDays: "Du ziehst in {days} Tagen ein.",
        heroEnding: "Noch {days} Tage in {property}.",
        stayMoveInOn: "Einzug am {date}",
        floorLine: "Etage {floor}",
        stayMoveInDays: "Einzug in {days} Tagen",
        stayDayOf: "Tag {day} von {total}",
        stayDaysLeft: "Noch {days} Tage",
        journeyApplication: "Bewerbung",
        journeyContract: "Vertrag",
        journeyDeposit: "Kaution & SEPA",
        journeyMoveIn: "Einzug",
        journeyMovedIn: "Eingezogen",
        quickReportIssue: "Problem melden",
        quickCommunity: "WG-Chat",
        quickReferrals: "Freunde empfehlen",
        ctaSign: "Vertrag ansehen",
        ctaDeposit: "Zu den Zahlungen",
        ctaMoveIn: "Einzug vorbereiten",
        ctaMoveOut: "Termin buchen",
        ctaProtocol: "Protokoll ausfüllen",
        ctaKeyReturn: "Zur Schlüsselrückgabe",
        ctaOverdue: "Zu den Zahlungen",
        ctaSupport: "Zur Hilfe",
        ctaReferralReward: "Zu den Empfehlungen",
        appliedEyebrow: "Wir sind dran",
        appliedTitle: "Wir prüfen deine Bewerbung.",
        appliedBody:
          "Du hörst per E-Mail und hier im Dashboard von uns. Gerade ist nichts zu tun.",
        actionNeeded: "Aktion erforderlich",
        searchPlaceholderTenant: "Suchen, z. B. Übergabeprotokoll",
        searchPlaceholderApplicant: "Suchen, z. B. SCHUFA",
        searchNothingFound: "Nichts gefunden. Frag uns unter Hilfe.",
        searchLabel: "Dashboard durchsuchen",
        // Search results: where each one lives, then its title (COL-623).
        searchWhereRoomMove: "Mein Zimmer · Ein- & Auszug",
        searchWhereRoomContract: "Mein Zimmer · Vertrag",
        searchWhereRoomPayments: "Mein Zimmer · Zahlungen",
        searchWhereRoomInfo: "Mein Zimmer · Infos & Dokumente",
        searchWhereHelp: "Hilfe",
        searchWhereHelpFaq: "Hilfe · FAQ",
        searchWhereCommunity: "Community",
        searchWhereReferrals: "Empfehlungen",
        searchWhereApplications: "Bewerbungen",
        searchWhereApplicationsDocuments: "Bewerbungen · Deine Unterlagen",
        searchWhereSaved: "Favoriten",
        searchResultHandoverReport: "Übergabeprotokoll",
        searchResultMoveOut: "Auszug & Abnahme",
        searchResultContractPdf: "Mietvertrag als PDF",
        searchResultRentPayments: "Miete & Zahlungen",
        searchResultSepa: "SEPA-Lastschriftmandat",
        searchResultDeposit: "Kaution",
        searchResultWifi: "WLAN-Zugang",
        searchResultHouseRules: "Hausordnung",
        searchResultWasteSorting: "Mülltrennung",
        searchResultLandlordConfirmation: "Wohnungsgeberbestätigung",
        searchResultBroadcastingFee: "Rundfunkbeitrag",
        searchResultReportIssue: "Problem melden",
        searchResultCleaningRota: "Putzplan",
        searchResultFlatChat: "WG-Chat",
        searchResultSharedCosts: "Gemeinsame Kosten",
        searchResultReferralCode: "Empfehlungscode",
        searchResultUploadDocuments: "Unterlagen hochladen",
        searchResultProofOfIncome: "Einkommensnachweis",
        searchResultApplicationStatus: "Bewerbungsstatus",
        searchResultSavedRooms: "Gemerkte Zimmer",
        searchResultRequestViewing: "Besichtigung anfragen",
        searchResultWhatsIncluded: "Was ist in der Miete enthalten?",
        searchResultMultipleApplications: "Mehrere Bewerbungen",
        tileNextRent: "Nächste Miete",
        // The overview's third tile: open requests, linking into Hilfe.
        tileRequests: "Deine Anfragen",
        tileRequestsEmpty: "Nichts gemeldet.",
        tileRequestsMore: "Alle Anfragen ansehen",
        tileRequestsOpen: "offen",
        tileDebitOn: "Einzug am {date}",
        tileDueOn: "Fällig am {date}",
        savedTitle: "Gemerkt.",
        savedViewAll: "Alle Zimmer ansehen",
        savedFreeFrom: "Frei ab {date}",
        savedAvailableNow: "Sofort frei",
        savedSqm: "{size} m²",
        savedRemove: "Entfernen",
        savedApply: "Bewerben",
        savedApplied: "Beworben",
        savedAllIn: "{price} warm",
        // Empfehlungen (COL-533 visual port)
        referralHeadline: "Freunde empfehlen, {amount} pro Einzug sparen",
        referralCopy: "Kopieren",
        referralCopied: "Kopiert",
        referralShareWhatsApp: "Per WhatsApp teilen",
        referralCopyLink: "Link kopieren",
        referralHowItWorks: "So funktioniert\u2019s",
        referralStep1: "Code teilen",
        referralStep1Body: "Per WhatsApp oder Link.",
        referralStep2: "Bewerbung",
        referralStep2Body: "Dein Freund gibt den Code bei der Bewerbung an.",
        referralStep3: "Vertrag",
        referralStep3Body: "Z\u00e4hlt, sobald der Vertrag unterschrieben ist.",
        referralStep4: "{amount} f\u00fcr dich",
        referralStep4Body:
          "Wird automatisch von deiner n\u00e4chsten Miete abgezogen.",
        referralYours: "Deine Empfehlungen",
        referralSavedSoFar: "Bisher gespart: {amount}",
        referralNobodyYet:
          "Noch niemand hat deinen Code benutzt. Sobald sich jemand bewirbt, siehst du hier den Status.",
        referralCodeFailed: "Dein Empfehlungscode konnte nicht geladen werden.",
        referralListFailed: "Deine Empfehlungen konnten nicht geladen werden.",
        referralShareText:
          "Zieh mit mir bei coliviq ein. Gib bei der Bewerbung meinen Code {code} an.",
        bodySign:
          "Prüfe die Angaben und unterschreibe digital. Danach: Kaution und SEPA-Mandat.",
        bodyDeposit:
          "Die Kaution von {amount} sichert dein Zimmer. Mit dem SEPA-Mandat wird die Miete automatisch eingezogen.",
        bodyOverdue:
          "Eine Miete ist noch offen. Schau kurz rein, damit keine Mahngebühren dazukommen.",
        bodyMoveIn:
          // "am Tag davor", not "drei Tage vorher": KEYNEST_UNLOCK_DAYS_BEFORE
          // is 1, so the pickup details unlock at 00:01 Berlin time the day
          // before move-in. The old wording sent a tenant looking three days
          // out, finding nothing, and asking support why.
          "Ort und Zeit der Schlüsselübergabe bekommst du am Tag vor deinem Einzug. Am Einzugstag füllst du das Übergabeprotokoll aus.",
        bodyMoveOut:
          "Buche einen Termin für die Endabnahme. Die Kaution erstatten wir nach dem Auszugsprotokoll.",
        bodyMoveInReport:
          "Halte den Zustand deines Zimmers am Einzugstag fest — so gibt es später keine Diskussion über Schäden, die schon da waren.",
        bodyMoveOutReport:
          "Vergiss nicht, dein Auszugsprotokoll auszufüllen, bevor du gehst — sonst drohen Abzüge von deiner Kaution.",
        bodyKeyReturn:
          "Dein Auszugsprotokoll ist eingereicht. Gib die Schlüssel zurück, damit wir die Kaution abrechnen können.",
        bodySupport: "Es gibt eine Antwort auf deine Anfrage.",
        bodyReferralReward:
          "Bei deiner Empfehlungsprämie stimmt etwas nicht — wir klären das mit dir.",
      }
    : {
        // Tabs — tenant
        tabOverview: "Overview",
        tabRoom: "My room",
        tabCommunity: "Community",
        tabReferrals: "Referrals",
        tabHelp: "Help",
        // Tabs — applicant
        tabApplications: "Applications",
        tabSaved: "Saved",
        // Hero
        greetingMorning: "Good morning, {name}.",
        greetingAfternoon: "Good afternoon, {name}.",
        greetingEvening: "Good evening, {name}.",
        nextStepEyebrow: "Your next step",
        allDoneEyebrow: "All done",
        allDoneTitle: "Welcome home.",
        allDoneBody:
          "Nothing needs you right now. If something breaks, report it under Help.",
        openAction: "Take care of it",
        actionSign: "Your rental contract is ready.",
        actionDeposit: "Pay the deposit and set up SEPA.",
        actionOverdue: "Rent is overdue",
        actionMoveIn: "You move in on {date}.",
        actionMoveOut: "Move-out on {date}.",
        actionMoveInReport: "Fill in your move-in report.",
        actionKeyReturn: "Return your keys.",
        actionSupport: "Read the reply from support",
        actionReferralReward: "Sort out your referral reward",
        // Room summary
        roomAt: "{room} · {address}",
        moveInOn: "Move-in on {date}",
        // States
        loading: "Loading …",
        loadFailed:
          "Your dashboard could not be loaded. Please reload the page.",
        noBooking:
          "There is no booking on your account yet. Once your application is accepted, your contract appears here.",
        openClassicView:
          "Applications and saved rooms in the previous dashboard",
        settingsDocumentsHandoff:
          "Your documents are managed in the previous dashboard for now.",
        settingsDocumentsOpen: "Open documents",
        // Footer — see the German half.
        footerTenantTitle: "Something not right? Let us know.",
        footerTenantAction: "Report an issue",
        footerApplicantTitle: "Haven\u2019t found the right one yet?",
        footerApplicantAction: "Find a room",
        footerWriteUs: "Write to us",
        footerCompany: "coliviq GmbH \u00b7 Elvirastra\u00dfe 10, 80636 Munich",
        footerPrivacy: "Privacy",
        footerImprint: "Legal notice",
        footerTerms: "Terms",
        footerCookieSettings: "Cookie settings",
        footerLogout: "Log out",
        // Applicant frame (Applications / Saved)
        applicationsHeading: "Your applications",
        applicationsEmpty:
          "You have no application running yet. Find a room and apply in a couple of minutes.",
        applicationsFindRoom: "Browse rooms",
        stepSubmitted: "Submitted",
        stepInReview: "In review",
        stepOffer: "Offer",
        stepContract: "Contract",
        allInFrom: "{price} all-in \u00b7 from {date}",
        allIn: "{price} all-in",
        allInFree: "{price} all-in \u00b7 free from {date}",
        documentsHeading: "Your documents",
        documentsProgress:
          "{done} of {total} uploaded. Complete documents improve your chances of getting a room.",
        documentsIdentity: "ID",
        documentsIdentityHint: "Front and back",
        documentsIncome: "Proof of income",
        documentsIncomeHint:
          "Last 3 payslips. Students: enrolment certificate or guarantor",
        documentsFilesCount: "{count} files",
        documentsOneFile: "1 file",
        documentsManage: "Manage documents",
        documentsAddFile: "Add another file",
        documentsAddFirstFile: "Add a file",
        documentsUploading: "Uploading …",
        documentsUploadFailed:
          "That file could not be uploaded. Please try again.",
        documentsAccepted: "PDF or image, up to 10MB.",
        documentsRemoveElsewhere: "Need a file removed? Ask us from Help.",
        savedHeading: "Saved rooms",
        savedEmpty:
          "You have not saved any rooms yet. Tap the heart while browsing and they will be here.",
        // Übersicht hero (COL-533 visual port)
        heroFromDate: "Your room from {date}.",
        heroMoveInDays: "You move in {days} days from now.",
        heroEnding: "{days} more days at {property}.",
        stayMoveInOn: "Move-in on {date}",
        floorLine: "Floor {floor}",
        stayMoveInDays: "Move-in in {days} days",
        stayDayOf: "Day {day} of {total}",
        stayDaysLeft: "{days} days left",
        journeyApplication: "Application",
        journeyContract: "Contract",
        journeyDeposit: "Deposit & SEPA",
        journeyMoveIn: "Move-in",
        journeyMovedIn: "Moved in",
        quickReportIssue: "Report an issue",
        quickCommunity: "Flat chat",
        quickReferrals: "Refer friends",
        ctaSign: "Review contract",
        ctaDeposit: "Go to payments",
        ctaMoveIn: "Prepare move-in",
        ctaMoveOut: "Book appointment",
        ctaProtocol: "Fill in report",
        ctaKeyReturn: "Key return details",
        ctaOverdue: "Go to payments",
        ctaSupport: "Go to Help",
        ctaReferralReward: "Go to Referrals",
        appliedEyebrow: "We\u2019re on it",
        appliedTitle: "We\u2019re reviewing your application.",
        appliedBody:
          "You\u2019ll hear from us by email and here in the dashboard. Nothing to do right now.",
        actionNeeded: "Action needed",
        searchPlaceholderTenant: "Search, e.g. handover report",
        searchPlaceholderApplicant: "Search, e.g. SCHUFA",
        searchNothingFound: "Nothing found. Ask us under Help.",
        searchLabel: "Search the dashboard",
        // Search results: where each one lives, then its title (COL-623).
        searchWhereRoomMove: "My room · Move-in & move-out",
        searchWhereRoomContract: "My room · Contract",
        searchWhereRoomPayments: "My room · Payments",
        searchWhereRoomInfo: "My room · Info & documents",
        searchWhereHelp: "Help",
        searchWhereHelpFaq: "Help · FAQ",
        searchWhereCommunity: "Community",
        searchWhereReferrals: "Referrals",
        searchWhereApplications: "Applications",
        searchWhereApplicationsDocuments: "Applications · Your documents",
        searchWhereSaved: "Saved",
        searchResultHandoverReport: "Handover report",
        searchResultMoveOut: "Move-out & inspection",
        searchResultContractPdf: "Rental contract as PDF",
        searchResultRentPayments: "Rent & payments",
        searchResultSepa: "SEPA mandate",
        searchResultDeposit: "Deposit",
        searchResultWifi: "Wi-Fi login",
        searchResultHouseRules: "House rules",
        searchResultWasteSorting: "Waste sorting",
        searchResultLandlordConfirmation:
          "Landlord confirmation (Wohnungsgeberbestätigung)",
        searchResultBroadcastingFee: "Broadcasting fee",
        searchResultReportIssue: "Report an issue",
        searchResultCleaningRota: "Cleaning rota",
        searchResultFlatChat: "Flat chat",
        searchResultSharedCosts: "Shared costs",
        searchResultReferralCode: "Referral code",
        searchResultUploadDocuments: "Upload documents",
        searchResultProofOfIncome: "Proof of income",
        searchResultApplicationStatus: "Application status",
        searchResultSavedRooms: "Saved rooms",
        searchResultRequestViewing: "Request a viewing",
        searchResultWhatsIncluded: "What\u2019s included in the rent?",
        searchResultMultipleApplications: "Multiple applications",
        tileNextRent: "Next rent",
        tileRequests: "Your requests",
        tileRequestsEmpty: "Nothing reported.",
        tileRequestsMore: "See all requests",
        tileRequestsOpen: "open",
        tileDebitOn: "Debit on {date}",
        tileDueOn: "Due on {date}",
        savedTitle: "Saved.",
        savedViewAll: "View all rooms",
        savedFreeFrom: "Free from {date}",
        savedAvailableNow: "Available now",
        savedSqm: "{size} m²",
        savedRemove: "Remove",
        savedApply: "Apply",
        savedApplied: "Applied",
        savedAllIn: "{price} all-in",
        // Referrals (COL-533 visual port)
        referralHeadline: "Refer friends, save {amount} per move-in",
        referralCopy: "Copy",
        referralCopied: "Copied",
        referralShareWhatsApp: "Share via WhatsApp",
        referralCopyLink: "Copy link",
        referralHowItWorks: "How it works",
        referralStep1: "Share code",
        referralStep1Body: "Via WhatsApp or link.",
        referralStep2: "Application",
        referralStep2Body: "Your friend enters the code when applying.",
        referralStep3: "Contract",
        referralStep3Body: "Counts once the contract is signed.",
        referralStep4: "{amount} for you",
        referralStep4Body: "Automatically deducted from your next rent.",
        referralYours: "Your referrals",
        referralSavedSoFar: "Saved so far: {amount}",
        referralNobodyYet:
          "No one has used your code yet. Once someone applies, you\u2019ll see the status here.",
        referralCodeFailed: "Your referral code could not be loaded.",
        referralListFailed: "Your referrals could not be loaded.",
        referralShareText:
          "Move in with me at coliviq. Enter my code {code} when you apply.",
        bodySign:
          "Check the details and sign digitally. Next up: deposit and SEPA mandate.",
        bodyDeposit:
          "The {amount} deposit secures your room. With the SEPA mandate, rent is collected automatically.",
        bodyOverdue:
          "A month of rent is still open. Take a look so no late fees are added.",
        bodyMoveIn:
          "You\u2019ll get the key pick-up place and time the day before your move-in. On move-in day you fill in the handover report.",
        bodyMoveOut:
          "Book a time for the final inspection. We refund the deposit after the move-out report.",
        bodyMoveInReport:
          "Record the state of your room on move-in day — that way there is no argument later about damage that was already there.",
        bodyMoveOutReport:
          "Don’t forget to fill in your move-out report before you leave, to avoid deductions from your deposit.",
        bodyKeyReturn:
          "Your move-out report is filed. Return the keys so we can settle your deposit.",
        bodySupport: "There is an answer waiting on your request.",
        bodyReferralReward:
          "Something is off with your referral reward — we will sort it out with you.",
      };
}
